import { ArrowLeft, Check, Copy, Moon, Palette } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

const colors = [
  ["Base background", "#101013", "--bg-base"],
  ["Sidebar", "#0C0D0F", "--sidebar-bg"],
  ["Surface", "#17181C", "--color-soft"],
  ["Elevated", "#1E1F24", "--color-elevated"],
  ["Border", "#2A2B31", "--color-line"],
  ["Primary text", "#EDEDEF", "--color-ink"],
  ["Secondary text", "#A0A1A8", "--color-ink-2"],
  ["Muted text", "#85868E", "--color-ink-3"],
  ["Accent", "#FF8A4C", "--color-brand"],
  ["Solid action", "#E8590C", "button-only"],
  ["Amber data", "#F5B85C", "chart"],
  ["Teal data", "#4FD1C5", "chart"],
];

const statuses = [
  ["Running", "#4ADE80"],
  ["Scheduled", "#60A5FA"],
  ["Paused", "#FBBF24"],
  ["Completed", "#9CA3AF"],
  ["Failed", "#F87171"],
];

export function TokenSheet() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState("");

  const copy = async (value: string) => {
    await navigator.clipboard?.writeText(value);
    setCopied(value);
    window.setTimeout(() => setCopied(""), 1200);
  };

  return (
    <div className="min-h-full bg-canvas px-6 py-8 text-ink md:px-10">
      <div className="mx-auto max-w-6xl">
        <button onClick={() => navigate("/")} className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-ink-2 hover:text-brand">
          <ArrowLeft className="h-4 w-4" /> Back to dashboard
        </button>
        <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-brand">Phase 1 / token sheet</p>
            <h1 className="text-3xl font-bold tracking-tight">Warm Graphite</h1>
            <p className="mt-2 max-w-xl text-sm text-ink-2">The dark skin in one frame. Every color maps to a CSS token so this concept can ship without a visual rewrite.</p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-line bg-elevated px-3 py-2 font-mono text-xs text-ink-2">
            <Moon className="h-4 w-4 text-brand" /> dark alternate skin
          </div>
        </div>

        <section className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-xl border border-line bg-elevated p-5">
            <div className="mb-5 flex items-center gap-2"><Palette className="h-4 w-4 text-brand" /><h2 className="font-bold">Core palette</h2></div>
            <div className="grid gap-2 sm:grid-cols-2">
              {colors.map(([label, value, token]) => (
                <button key={label} onClick={() => copy(value)} className="group flex items-center gap-3 rounded-lg border border-line bg-soft p-3 text-left hover:border-brand/60">
                  <span className="h-9 w-9 shrink-0 rounded-md border border-white/10" style={{ background: value }} />
                  <span className="min-w-0 flex-1"><span className="block text-sm font-semibold">{label}</span><span className="font-mono text-[10px] text-ink-3">{token} · {value}</span></span>
                  {copied === value ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-3.5 w-3.5 text-ink-3 opacity-0 group-hover:opacity-100" />}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-line bg-elevated p-5">
            <h2 className="mb-5 font-bold">Status colors</h2>
            <div className="space-y-3">
              {statuses.map(([label, value]) => (
                <div key={label} className="flex items-center gap-3 rounded-lg border border-line bg-soft px-3 py-3">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: value, boxShadow: `0 0 12px ${value}` }} />
                  <span className="flex-1 text-sm font-semibold">{label}</span><span className="font-mono text-xs text-ink-3">{value}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 border-t border-line pt-5">
              <p className="mb-3 font-mono text-[10px] font-bold uppercase tracking-wider text-ink-3">Treatment notes</p>
              <ul className="space-y-2 text-sm text-ink-2"><li>2px orange glow strips on KPI and kanban columns</li><li>Editor inset uses #0C0D0F with orange section headers</li><li>Dashed drop zones brighten on hover</li></ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
